import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FaBars, FaTimes } from 'react-icons/fa';
import { AiFillFilePpt } from 'react-icons/ai';
import { MdManageSearch } from 'react-icons/md';
// import { MdLogout } from 'react-icons/md'; // 기존 로그아웃 아이콘 - 새 버튼으로 대체
import { BsPalette2 } from 'react-icons/bs';
import { TbSettings, TbReceipt } from 'react-icons/tb';
import { createAppStyles } from '../styles/appStyles';
import fourbroIcon from '../forbro_icon.png';
import LogoutButton from './LogoutButton';

const Sidebar = ({
  sidebarOpen,
  setSidebarOpen,
  sidebarWidth,
  isMobile,
  // 새로운 props
  displayName,
  userCredit,
  pptActiveTab,
  setPptActiveTab,
  CLOUD_FUNCTION_URL,
  showConfirmModal,
  showAlertModal,
  navigate,
  // 프로젝트 화면 관련 props
  activeView,
  setActiveView,
}) => {
  const { t } = useTranslation();
  const styles = createAppStyles(sidebarWidth, sidebarOpen, isMobile);

  // 분석도구 하위메뉴 열림 상태 (기본값 false - 닫힘)
  const [analysisMenuOpen, setAnalysisMenuOpen] = useState(false);
  // hover 시 임시로 열림 (한 번 열리면 클릭 전까지 유지)
  const [analysisMenuHoverOpened, setAnalysisMenuHoverOpened] = useState(false);

  // 현재 활성 메뉴 - activeView prop과 연동
  const [activeMenu, setActiveMenu] = useState(activeView || 'analysis');

  // activeView가 변경되면 activeMenu도 동기화
  useEffect(() => {
    if (activeView) {
      setActiveMenu(activeView);
      // 분석도구로 전환될 때 하위메뉴 자동 열기
      if (activeView === 'analysis') {
        setAnalysisMenuOpen(true);
        setAnalysisMenuHoverOpened(false);
      }
    }
  }, [activeView]);

  // 로그아웃 핸들러 → 딸깍 로그인 페이지로 리다이렉트
  const handleLogout = async () => {
    const confirmed = await showConfirmModal(t('sidebar.logoutConfirm'));
    if (confirmed) {
      console.log('🔓 딸깍으로 로그아웃 리다이렉트');
      window.location.href = 'https://xn--il0b54k.net/auth/login';
    }
  };

  // 분석도구 하위메뉴 클릭 핸들러
  const handleAnalysisSubMenuClick = (tab) => {
    setActiveMenu('analysis');
    setPptActiveTab(tab);
    // 분석도구 화면으로 전환
    if (setActiveView) {
      setActiveView('analysis');
    }
    // 모바일에서 메뉴 선택 시 사이드바 자동 닫기
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  // 분석도구 메뉴 hover 핸들러 - hover 시 열림 (마우스 벗어나도 유지)
  const handleAnalysisMenuHover = () => {
    if (!analysisMenuOpen) {
      setAnalysisMenuHoverOpened(true);
    }
  };

  // 분석도구 메뉴 클릭 핸들러 - 클릭 시 토글 (닫기 가능)
  const handleAnalysisMenuClick = () => {
    setActiveMenu('analysis');
    if (analysisMenuOpen || analysisMenuHoverOpened) {
      // 열려있으면 닫기
      setAnalysisMenuOpen(false);
      setAnalysisMenuHoverOpened(false);
    } else {
      // 닫혀있으면 열기
      setAnalysisMenuOpen(true);
    }
  };

  // 하위메뉴 표시 여부
  const showSubMenu = analysisMenuOpen || analysisMenuHoverOpened;

  return (
    <>
      {/* 모바일 햄버거 메뉴 버튼 */}
      {isMobile && !sidebarOpen && (
        <button
          onClick={() => setSidebarOpen(true)}
          style={styles.hamburgerButton}
        >
          <FaBars />
        </button>
      )}

      {/* Sidebar */}
      <div style={styles.sidebar}>
        {/* 모바일 닫기 버튼 */}
        {isMobile && sidebarOpen && (
          <button
            onClick={() => setSidebarOpen(false)}
            style={styles.sidebarCloseButton}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <FaTimes />
          </button>
        )}

        {/* 로고 영역 */}
        <div style={styles.logo}>
          <img
            src="/ddalkkakcheck_text.png"
            alt="딸깍체크"
            style={styles.logoImage}
          />
        </div>

        {/* 네비게이션 메뉴 */}
        <nav style={styles.navMenu}>
          {/* 프로젝트 메뉴 */}
          <button
            style={styles.navItem(activeMenu === 'project')}
            onClick={() => {
              setActiveMenu('project');
              // 프로젝트 화면으로 전환
              if (setActiveView) {
                setActiveView('project');
              }
              // 모바일에서 메뉴 선택 시 사이드바 자동 닫기
              if (isMobile) {
                setSidebarOpen(false);
              }
            }}
            onMouseEnter={(e) => {
              if (activeMenu !== 'project') {
                e.currentTarget.style.backgroundColor = 'rgba(59, 130, 246, 0.05)';
              }
            }}
            onMouseLeave={(e) => {
              if (activeMenu !== 'project') {
                e.currentTarget.style.backgroundColor = 'transparent';
              }
            }}
          >
            <AiFillFilePpt style={{ fontSize: '20px', color: '#1A62EA' }} />
            <span>{t('sidebar.fileManagement')}</span>
          </button>

          {/* 분석도구 메뉴 */}
          <div onMouseEnter={handleAnalysisMenuHover}>
            <button
              style={styles.navItem(activeMenu === 'analysis')}
              onClick={handleAnalysisMenuClick}
              onMouseEnter={(e) => {
                if (activeMenu !== 'analysis') {
                  e.currentTarget.style.backgroundColor = 'rgba(59, 130, 246, 0.05)';
                }
              }}
              onMouseLeave={(e) => {
                if (activeMenu !== 'analysis') {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
            >
              <MdManageSearch style={{ fontSize: '22px', color: '#1A62EA' }} />
              <span>{t('sidebar.analysisTools')}</span>
              <span style={{
                marginLeft: 'auto',
                fontSize: '20px',
                transform: showSubMenu ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease'
              }}>
                ▼
              </span>
            </button>

            {/* 분석도구 하위메뉴 - hover로 열리고 클릭으로만 닫힘 */}
            {showSubMenu && (
              <div style={styles.subMenu}>
                <button
                  style={styles.subMenuItem(activeMenu === 'analysis' && pptActiveTab === 'typo')}
                  onClick={() => handleAnalysisSubMenuClick('typo')}
                  onMouseEnter={(e) => {
                    if (!(activeMenu === 'analysis' && pptActiveTab === 'typo')) {
                      e.currentTarget.style.backgroundColor = 'rgba(59, 130, 246, 0.05)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!(activeMenu === 'analysis' && pptActiveTab === 'typo')) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  {t('sidebar.typoCheck')}
                </button>
                <button
                  style={styles.subMenuItem(activeMenu === 'analysis' && pptActiveTab === 'color')}
                  onClick={() => handleAnalysisSubMenuClick('color')}
                  onMouseEnter={(e) => {
                    if (!(activeMenu === 'analysis' && pptActiveTab === 'color')) {
                      e.currentTarget.style.backgroundColor = 'rgba(59, 130, 246, 0.05)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!(activeMenu === 'analysis' && pptActiveTab === 'color')) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  {t('sidebar.colorCheck')}
                </button>
                <button
                  style={styles.subMenuItem(activeMenu === 'analysis' && pptActiveTab === 'font')}
                  onClick={() => handleAnalysisSubMenuClick('font')}
                  onMouseEnter={(e) => {
                    if (!(activeMenu === 'analysis' && pptActiveTab === 'font')) {
                      e.currentTarget.style.backgroundColor = 'rgba(59, 130, 246, 0.05)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!(activeMenu === 'analysis' && pptActiveTab === 'font')) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  {t('sidebar.fontCheck')}
                </button>
                <button
                  style={styles.subMenuItem(activeMenu === 'analysis' && pptActiveTab === 'translate')}
                  onClick={() => handleAnalysisSubMenuClick('translate')}
                  onMouseEnter={(e) => {
                    if (!(activeMenu === 'analysis' && pptActiveTab === 'translate')) {
                      e.currentTarget.style.backgroundColor = 'rgba(59, 130, 246, 0.05)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!(activeMenu === 'analysis' && pptActiveTab === 'translate')) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  {t('sidebar.translate')}
                </button>
              </div>
            )}
          </div>

          {/* 브랜드킷 메뉴 */}
          <button
            style={styles.navItem(activeMenu === 'brandkit')}
            onClick={() => {
              setActiveMenu('brandkit');
              setActiveView('brandkit');
              // 모바일에서 메뉴 선택 시 사이드바 자동 닫기
              if (isMobile) {
                setSidebarOpen(false);
              }
            }}
            onMouseEnter={(e) => {
              if (activeMenu !== 'brandkit') {
                e.currentTarget.style.backgroundColor = 'rgba(59, 130, 246, 0.05)';
              }
            }}
            onMouseLeave={(e) => {
              if (activeMenu !== 'brandkit') {
                e.currentTarget.style.backgroundColor = 'transparent';
              }
            }}
          >
            <BsPalette2 style={{ fontSize: '20px', color: '#1A62EA' }} />
            <span>{t('sidebar.brandKit')}</span>
          </button>

          {/* 크레딧 내역 메뉴 */}
          <button
            style={styles.navItem(activeMenu === 'creditHistory')}
            onClick={() => {
              setActiveMenu('creditHistory');
              if (setActiveView) {
                setActiveView('creditHistory');
              }
              // 모바일에서 메뉴 선택 시 사이드바 자동 닫기
              if (isMobile) {
                setSidebarOpen(false);
              }
            }}
            onMouseEnter={(e) => {
              if (activeMenu !== 'creditHistory') {
                e.currentTarget.style.backgroundColor = 'rgba(59, 130, 246, 0.05)';
              }
            }}
            onMouseLeave={(e) => {
              if (activeMenu !== 'creditHistory') {
                e.currentTarget.style.backgroundColor = 'transparent';
              }
            }}
          >
            <TbReceipt style={{ fontSize: '20px', color: '#1A62EA' }} />
            <span>{t('sidebar.creditHistory')}</span>
          </button>

          {/* 설정 메뉴 */}
          <button
            style={styles.navItem(activeMenu === 'settings')}
            onClick={() => {
              setActiveMenu('settings');
              if (setActiveView) {
                setActiveView('settings');
              }
              // 모바일에서 메뉴 선택 시 사이드바 자동 닫기
              if (isMobile) {
                setSidebarOpen(false);
              }
            }}
            onMouseEnter={(e) => {
              if (activeMenu !== 'settings') {
                e.currentTarget.style.backgroundColor = 'rgba(59, 130, 246, 0.05)';
              }
            }}
            onMouseLeave={(e) => {
              if (activeMenu !== 'settings') {
                e.currentTarget.style.backgroundColor = 'transparent';
              }
            }}
          >
            <TbSettings style={{ fontSize: '20px', color: '#1A62EA' }} />
            <span>{t('sidebar.settings')}</span>
          </button>
        </nav>

        {/* 사용자 프로필 영역 */}
        <div style={styles.userProfile}>
          <div style={styles.userInfo}>
            <img
              src={fourbroIcon}
              alt={t('common.user')}
              style={styles.userAvatar}
            />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <span style={styles.userName}>{displayName || t('common.user')}</span>
              <span style={{
                fontSize: '16px',
                color: '#6B7280',
                fontWeight: '500',
                padding: '2px 8px',
              }}>
                {t('sidebar.credit', { amount: userCredit !== undefined ? userCredit : '-' })}
              </span>
            </div>
          </div>
{/* 기존 로그아웃 버튼 - 주석처리
          <button
            style={styles.logoutButton}
            onClick={handleLogout}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
            title="로그아웃"
          >
            <MdLogout style={{ fontSize: '30px', color: '#6B7280' }} />
          </button>
          */}
          {/* 새로운 애니메이션 로그아웃 버튼 */}
          <LogoutButton onClick={handleLogout} />
        </div>
      </div>

      {/* 모바일 오버레이 */}
      {isMobile && sidebarOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 99
          }}
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </>
  );
};

export default Sidebar;
